import { fromJS, is, List, Map, Record } from '@gonfalon/immutable-shim/immutableShim';
import {
  isChecked,
  isJSON,
  isLength,
  isNotEmpty,
  optional,
  validateRecord,
} from '@gonfalon/legacy-form-validation/validationUtils';
import type { ImmutableMap } from '@gonfalon/legacy-immutable/immutableUtils';
import { isEmpty } from 'lodash';

import { allowDecision } from '#utils/access/allowDecision';
import { createAccessDecision } from '#utils/access/records/AccessDecision';
import type { AccessChecks, CheckAccessFunction } from '#utils/access/types';
import type { Link } from '#utils/linkUtils';
import type { Member } from '#utils/members/records/Member';
import { isCompletePolicyString, isPolicyString } from '#utils/policyUtils';
import { isAbsoluteURL, isValidURLPort } from '#utils/urlUtils';

export type WebhookRequestOptions = { pathOnDone?: string };

type WebhookProps = {
  _access?: AccessChecks;
  _links: ImmutableMap<{
    self: Link;
  }>;
  _id: string;
  name: string;
  url: string;
  sign: boolean;
  secret?: string;
  statements?: string;
  on: boolean;
  tags: List<string>;
};

export class Webhook extends Record<WebhookProps>({
  _access: undefined,
  _links: Map(),
  _id: '',
  name: '',
  url: '',
  sign: false,
  secret: undefined,
  statements: undefined,
  on: true,
  tags: List(),
}) {
  checkAccess({ profile }: { profile: Member }): CheckAccessFunction {
    const access = this.get('_access');

    if (profile.isReader()) {
      return () => createAccessDecision({ isAllowed: false, appliedRoleName: 'Reader' });
    }

    if (profile.isAdmin() || profile.isWriter() || profile.isOwner() || !access || !access.get('denied')) {
      return allowDecision;
    }

    return (action) => {
      const deniedAction = access.get('denied')?.find((v) => v.get('action') === action);
      if (deniedAction) {
        const reason = deniedAction.get('reason');
        const roleName = reason && reason.get('role_name');
        return createAccessDecision({
          isAllowed: false,
          appliedRoleName: roleName,
        });
      }
      return createAccessDecision({ isAllowed: true });
    };
  }

  selfLink() {
    return this.getIn(['_links', 'self', 'href']);
  }

  toForm() {
    return createWebhookForm({
      name: this.name,
      url: this.url,
      statements: this.statements ? JSON.stringify(this.statements, null, 2) : '',
      tags: this.tags,
      sign: this.sign,
      secret: this.secret || '',
      on: this.on,
      // We only want to get privacy policy approval for new webhooks. If we already have a webhook configuration
      // then we can assume the privacy policy has been approved
      privacyPolicyApproved: true,
    });
  }

  mergeForm(form: WebhookForm) {
    return this.withMutations((webhook) =>
      webhook.merge({
        name: form.name,
        url: form.url,
        statements: parseFormStatements(form.statements),
        tags: form.tags,
        secret: form.secret as string,
        sign: form.sign,
        on: form.on,
      }),
    );
  }
}

export type WebhookFormProps = {
  name: string;
  url: string;
  sign: boolean;
  secret: string | null;
  statements: string;
  on: boolean;
  tags: List<string>;
  privacyPolicyApproved: boolean;
};

// Intermediate model for webhook form with JSON statement value -- makes tracking dirty
// state trivial.
export class WebhookForm extends Record<WebhookFormProps>({
  name: '',
  url: '',
  sign: false,
  secret: '',
  statements: '',
  on: true,
  tags: List(),
  privacyPolicyApproved: false,
}) {
  validate() {
    return validateRecord(
      this,
      isChecked('privacyPolicyApproved'),
      optional(isNotEmpty)('name'),
      isLength(0, 100)('name'),
      isNotEmpty('url'),
      isAbsoluteURL('url'),
      isValidURLPort('url'),
      optional(isJSON)('statements'),
      optional(isPolicyString)('statements'),
      optional(isCompletePolicyString)('statements'),
    );
  }

  is(other: WebhookForm) {
    try {
      return (
        this.name === other.name &&
        this.url === other.url &&
        this.secret === other.secret &&
        ((isEmpty(this.statements) && isEmpty(other.statements)) ||
          is(fromJS(JSON.parse(this.statements)), fromJS(JSON.parse(other.statements)))) &&
        this.on === other.on &&
        is(this.tags, other.tags)
      );
    } catch {
      // could fail to parse as JSON
      return false;
    }
  }

  toWebhook() {
    return createWebhook({
      name: this.name,
      url: this.url,
      sign: this.sign,
      secret: !isEmpty(this.secret) ? this.secret : undefined,
      statements: parseFormStatements(this.statements),
      on: this.on,
      tags: this.tags,
    });
  }
}

function parseFormStatements(statements: string) {
  const parsed = isEmpty(statements) ? undefined : JSON.parse(statements);
  return parsed && parsed.length ? parsed : undefined;
}

export function createWebhook(props = {}) {
  return props instanceof Webhook ? props : new Webhook(props);
}

export function createWebhookForm(props = {}) {
  return props instanceof WebhookForm ? props : new WebhookForm(props);
}
